/**
 * MFA OTP Verification Screen
 * UI Component - Presentation layer only
 * Delegates all business logic to MFAController
 */

import React, { useState, useEffect, useRef } from "react";
import {
  CircularProgress,
  Alert,
  InputBase,
  InputAdornment,
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useSharedValue } from "../Constants/constants";
import arrowIcon from "../../assets/new icons/extraction review/right arrow.svg";
import doneRoundedIcon from "../../assets/new icons/DoneRounded.png";
import "./MFAOtp.css";

// Import controller for business logic (same folder)
import {
  MFA_CONFIG,
  handleOTPVerification,
  handleOTPResend,
  clearMFASession,
  getMFAPreferencesFromSession,
  getDeliveryDisplayInfo,
} from "./MFAController";

interface MFAOtpProps {
  history?: any;
}

const MFAOtp: React.FC<MFAOtpProps> = ({ history }) => {
  const { updateDirectNavigation } = useSharedValue();

  // In the two-step MFA model, JWT is NOT in localStorage yet.
  // MFA data is stored in sessionStorage by Login.js.
  const mfaUserId = Number(sessionStorage.getItem("mfaUserId") || "0");
  const mfaFirstName = sessionStorage.getItem("mfaFirstName") || "";

  // Get MFA preferences from session (set during login)
  const { showEmail, showPhone, mobileNumber } = getMFAPreferencesFromSession();

  // Single-field OTP state
  const [otpValue, setOtpValue] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState<boolean>(false);

  // Timer State
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(MFA_CONFIG.RESEND_TIMER_SECONDS);
  const [otpSent, setOtpSent] = useState<boolean>(false);

  // Refs for input focus management
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Initialize component - OTP already sent by backend after login
   */
  useEffect(() => {
    setOtpSent(true);
    setCanResend(false);
    setResendTimer(MFA_CONFIG.RESEND_TIMER_SECONDS);
  }, []);

  /**
   * OTP expiry timer — fires after OTP_EXPIRY_SECONDS (10 min)
   * Shows a dialog prompting user to go back to login
   */
  useEffect(() => {
    const expiryTimeout = setTimeout(() => {
      if (!success) {
        setIsSessionExpired(true);
        clearMFASession();
        setShowExpiryDialog(true);
      }
    }, MFA_CONFIG.OTP_EXPIRY_SECONDS * 1000);

    return () => clearTimeout(expiryTimeout);
  }, []);

  /**
   * Resend timer countdown
   */
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer, canResend]);

  /**
   * Auto-focus first input on mount
   */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /**
   * Auto-verify when OTP reaches correct length
   */
  useEffect(() => {
    if (MFA_CONFIG.AUTO_VERIFY_ON_COMPLETE && otpValue.length === MFA_CONFIG.OTP_LENGTH) {
      setError("");
      verifyOTP(otpValue);
    }
  }, [otpValue]);

  /**
   * Handle OTP input change
   */
  const handleOtpChange = (value: string) => {
    // Only allow digits, up to OTP_LENGTH
    const digits = value.replace(/\D/g, "").slice(0, MFA_CONFIG.OTP_LENGTH);
    setOtpValue(digits);
  };

  /**
   * Handle key down events
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && otpValue.length === MFA_CONFIG.OTP_LENGTH) {
      handleVerifyClick();
    }
  };

  /**
   * Handle paste event
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, MFA_CONFIG.OTP_LENGTH);
    if (pasted) {
      setError("");
      setOtpValue(pasted);
    }
  };

  /**
   * Verify OTP with backend
   */
  const verifyOTP = async (otpCode: string) => {
    try {
      setIsVerifying(true);
      setError("");

      const result = await handleOTPVerification(otpCode.split(""));

      if (result.success) {
        setSuccess(true);
        clearMFASession();
        setTimeout(() => {
          updateDirectNavigation(true);
          history.push({
            pathname: "/dashboard",
            state: { someValue: true },
          });
        }, MFA_CONFIG.REDIRECT_DELAY_MS);
      } else {
        if (result.errorCode === "MFA_LOCKOUT") {
          setError(result.message || "Account locked. Please try again later.");
          clearMFASession();
          setTimeout(() => {
            history.push("/login");
          }, 3000);
          return;
        }
        const msg = result.message || "Invalid OTP. Please try again.";
        if (msg.toLowerCase().includes("session expired")) {
          setIsSessionExpired(true);
          clearMFASession();
        }
        setError(msg);
        setOtpValue("");
        inputRef.current?.focus();
      }
    } catch (err: any) {
      console.error("❌ Exception in verifyOTP:", err);
      setError("An error occurred. Please try again.");
      setOtpValue("");
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * Handle manual verify button click
   */
  const handleVerifyClick = () => {
    if (otpValue.length === MFA_CONFIG.OTP_LENGTH) {
      verifyOTP(otpValue);
    } else {
      setError(`Please enter all ${MFA_CONFIG.OTP_LENGTH} digits`);
    }
  };

  /**
   * Handle resend OTP
   */
  const handleResend = async () => {
    if (!canResend || isSending) return;

    try {
      setIsSending(true);
      setError("");

      const result = await handleOTPResend(mfaUserId, showEmail, showPhone);

      if (result.success) {
        setOtpSent(true);
        setCanResend(false);
        setResendTimer(MFA_CONFIG.RESEND_TIMER_SECONDS);
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        // Sync timer with server-enforced cooldown if provided
        if (result.retryAfterSeconds) {
          setCanResend(false);
          setResendTimer(result.retryAfterSeconds);
        }
        const msg = result.message || "Failed to send OTP. Please try again.";
        if (msg.toLowerCase().includes("session expired")) {
          setIsSessionExpired(true);
          clearMFASession();
        }
        setError(msg);
      }
    } catch (err: any) {
      console.error("Error resending OTP:", err);
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handle cancel/go back
   */
  const handleCancel = () => {
    clearMFASession();
    history.push("/login");
  };

  /**
   * Get delivery info for display
   */
  const deliveryInfo = getDeliveryDisplayInfo(showEmail, showPhone, mobileNumber, mfaFirstName);

  const fieldSx = {
    bgcolor: "#ffffff",
    borderRadius: "3.56px",
    border: "0.89px solid #DDDDDD",
    px: "10.68px",
    height: "42.73px",
    fontSize: "14.33px",
    fontFamily: "Poppins, sans-serif",
    color: "#6A6A6B",
    width: "100%",
    "& input": { padding: 0, letterSpacing: "0.2em" },
    "& input::placeholder": { color: "#6A6A6B", letterSpacing: "normal" },
  };

  return (
    <Box
      className="mfa-otp-container"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 5, borderRadius: 4, bgcolor: "#fff", maxWidth: 580, width: "100%", mx: 2 }}
      >
        {/* Title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            width: "100%",
            justifyContent: "center",
          }}
        >
          <img
            src={doneRoundedIcon}
            alt=""
            style={{ width: 28, height: 28, filter: "var(--icon-download-filter)" }}
          />
          <Typography
            sx={{
              fontSize: "26px",
              fontWeight: 500,

              fontFamily: "Poppins, sans-serif",
              color: "#111111",
            }}
          >
            Enter Verification Code
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "13px",
            fontFamily: "Poppins, sans-serif",
            color: "#6A6A6B",
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          {deliveryInfo.text}
          <br />
          Please enter the verification code.
        </Typography>

        {/* OTP field */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#6A6A6B",
              mb: 0.75,
              fontSize: "14.33px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
            }}
          >
            Verification Code
          </Typography>
          <InputBase
            inputRef={inputRef}
            value={otpValue}
            onChange={(e) => handleOtpChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Please enter the verification code"
            fullWidth
            disabled={isVerifying}
            startAdornment={
              <InputAdornment position="start">
                <img
                  src={doneRoundedIcon}
                  alt=""
                  style={{
                    width: 18,
                    height: 18,
                    marginRight: "6px",
                    filter: "var(--icon-download-filter)",
                  }}
                />
              </InputAdornment>
            }
            sx={fieldSx}
            inputProps={{ maxLength: MFA_CONFIG.OTP_LENGTH, style: { padding: 0 } }}
          />
        </Box>

        {/* Resend link */}
        <Box sx={{ textAlign: "right", mb: 2 }}>
          {isSessionExpired ? (
            <Typography
              variant="body2"
              onClick={handleCancel}
              sx={{
                color: "#2aa7e0",
                fontSize: "16px",
                cursor: "pointer",
                fontFamily: "Poppins, sans-serif",
                textDecoration: "underline",
              }}
            >
              Back to Login
            </Typography>
          ) : canResend ? (
            <Typography
              variant="body2"
              onClick={handleResend}
              sx={{
                color: isSending ? "#999" : "#2aa7e0",
                fontSize: "16px",
                cursor: isSending ? "default" : "pointer",
                fontFamily: "Poppins, sans-serif",
                textDecoration: isSending ? "none" : "underline",
                "&:hover": { textDecoration: isSending ? "none" : "underline" },
              }}
            >
              {isSending ? "Sending..." : "Resend Verification Code"}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "#999", fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
            >
              Resend code in {resendTimer}s
            </Typography>
          )}
        </Box>

        {/* Errors / success */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: "Poppins, sans-serif" }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, fontFamily: "Poppins, sans-serif" }}>
            Verified successfully!
          </Alert>
        )}
        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 2, fontFamily: "Poppins, sans-serif" }}>
            Code resent successfully!
          </Alert>
        )}

        {/* Verify button */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", marginTop: "40px" }}>
          <Button
            onClick={handleVerifyClick}
            variant="contained"
            disabled={isVerifying || isSessionExpired || otpValue.length !== MFA_CONFIG.OTP_LENGTH}
            className="mfa-mui-verify-btn"
          >
            {isVerifying ? (
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ flex: 1 }} />
                <CircularProgress size={14} thickness={5} sx={{ color: "#fff", mr: 1 }} />
                <span>Verifying...</span>
                <Box sx={{ flex: 1 }} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ flex: 1 }} />
                <span>Verify</span>
                <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                  <img src={arrowIcon} alt="" style={{ width: 12, height: 12, padding: 0 }} />
                </Box>
              </Box>
            )}
          </Button>
        </Box>
      </Paper>

      {/* OTP Expiry Dialog */}
      <Dialog
        open={showExpiryDialog}
        sx={{
          "& .MuiDialog-paper": { borderRadius: "12px", p: 1, minWidth: 340, textAlign: "center" },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "18px" }}>
          Verification Code Expired
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: "#424242" }}
          >
            Your verification code has expired after 10 minutes. Please log in again to receive a
            new code.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => {
              setShowExpiryDialog(false);
              history.push("/login");
            }}
            variant="contained"
            sx={{
              backgroundColor: "#111111",
              fontFamily: "Poppins, sans-serif",
              borderRadius: "8px",
              px: 4,
              "&:hover": { backgroundColor: "#333333" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MFAOtp;
